import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import moment from "moment/moment";
import { MouseEvent, useCallback, useEffect, useState } from "react";
import {
  AddTeamMemberMutationMutation,
  AddTeamMemberMutationMutationVariables,
  GetOrganizationMemberQueryQuery,
  GetOrganizationMemberQueryQueryVariables,
  OrganizationMemberFragment,
  OrganizationRole,
  PricingTier,
  RemoveTeamMemberMutationMutation,
  RemoveTeamMemberMutationMutationVariables,
  UpdateMemberMutationMutation,
  UpdateMemberMutationMutationVariables,
  UpdateUserSettingsMutation,
  UpdateUserSettingsMutationVariables,
} from "types/graphql-schema";

import updateUserMutation from "@apps/account/graphql/update-user-mutation";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import DeprecatedSelect, { SelectOption } from "@components/select/select";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDebounce from "@components/use-debounce/use-debounce";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import addTeamMemberMutation from "../graphql/add-team-member-mutation";
import getOrganizationMemberQuery from "../graphql/get-organization-member-query";
import getOrganizationMembersQuery from "../graphql/get-organization-members-query";
import removeTeamMemberMutation from "../graphql/remove-team-member-mutation";
import updateMemberMutation from "../graphql/update-member-mutation";
import MemberManagerCombobox from "./member-manager-combobox";

const roleOptions: SelectOption<OrganizationRole>[] = [
  {
    value: OrganizationRole.Member,
    label: "User",
    description:
      "Standard user with access to their own data and assigned workflows.",
  },
  {
    value: OrganizationRole.ItAdmin,
    label: "IT Admin",
    description:
      "Manages integrations and connected tools. No access to people or review data.",
  },
  {
    value: OrganizationRole.PeopleAdmin,
    label: "People Admin",
    description:
      "Access to all org data (except their own). Manages people, reviews, and workflows.",
  },
  {
    value: OrganizationRole.GlobalAdmin,
    label: "Global Admin",
    description:
      "Access to all settings and data (including themselves and their managers).",
  },
];

const getRoleLabel = (role: OrganizationRole | null | undefined) => {
  if (!role) {
    return "";
  }

  const option = roleOptions.find((opt) => opt.value === role);
  if (!option) {
    return "";
  }

  return typeof option.label === "string" ? option.label : "";
};

const MemberEditDialogContent = ({
  organization,
  member,
  onClose,
}: {
  organization: any;
  member: OrganizationMemberFragment;
  onClose: () => void;
}) => {
  const label = useLabel();
  const [updateMember, { loading: loadingMutation }] = useMutation<
    UpdateMemberMutationMutation,
    UpdateMemberMutationMutationVariables
  >(updateMemberMutation);
  const [updateUser, { loading: loadingUpdateUser }] = useMutation<
    UpdateUserSettingsMutation,
    UpdateUserSettingsMutationVariables
  >(updateUserMutation, { refetchQueries: [getOrganizationMembersQuery] });
  const [addTeamMember] = useMutation<
    AddTeamMemberMutationMutation,
    AddTeamMemberMutationMutationVariables
  >(addTeamMemberMutation);
  const [removeTeamMember] = useMutation<
    RemoveTeamMemberMutationMutation,
    RemoveTeamMemberMutationMutationVariables
  >(removeTeamMemberMutation);

  const user = assertNonNull(member.user);
  const userId = user.id;
  const userName = user.name;

  const [employeeId, setEmployeeId] = useState(member.employeeId);
  const [firstName, setFirstName] = useState(member.user?.firstName);
  const [lastName, setLastName] = useState(member.user?.lastName);
  const [position, setPosition] = useState(member.position);
  const [location, setLocation] = useState(member.location);
  const debouncedEmployeeId = useDebounce(employeeId, 300);
  const debouncedFirstName = useDebounce(firstName, 300);
  const debouncedLastName = useDebounce(lastName, 300);
  const debouncedPosition = useDebounce(position, 300);
  const debouncedLocation = useDebounce(location, 300);
  const currentUser = useReactiveVar(currentUserVar);
  const currentOrganization = useReactiveVar(currentOrganizationVar);
  const canViewPermissions =
    currentOrganization?.userMembership?.role === OrganizationRole.GlobalAdmin;
  const canEditPermissions =
    canViewPermissions &&
    organization.effectivePricingTier !== PricingTier.Free;
  const {
    ConfirmationDialog: ConfirmRoleChangeDialog,
    confirm: confirmRoleChange,
    setTitle: setConfirmTitle,
    setMessage: setConfirmMessage,
  } = useConfirm("", "");

  const managers = member.user?.managers
    ? assertEdgesNonNull(member.user.managers)
    : [];
  const primaryManagerId = member.user?.primaryManager?.id;
  const teams = member.user?.teams ? assertEdgesNonNull(member.user.teams) : [];
  const managerIds = managers.map(({ id }: { id: number }) => id);
  const excludeUserIds = [userId, ...managerIds];

  const {
    options: teamOptions,
    loading: teamLoading,
    query,
    setQuery,
  } = useUserComboboxQuery({
    types: [UserComboboxOptionType.TEAM],
    excludeTeamIds: teams.map(({ id }) => id),
  });

  const handleChangeRole = useCallback(
    async (option: SelectOption<OrganizationRole>) => {
      if (!canEditPermissions) {
        return;
      }

      const adminRoles = new Set<OrganizationRole>([
        OrganizationRole.GlobalAdmin,
        OrganizationRole.PeopleAdmin,
        OrganizationRole.ItAdmin,
      ]);

      if (
        adminRoles.has(option.value) &&
        option.value !== member.role &&
        currentUser.id !== userId
      ) {
        const roleLabel = getRoleLabel(option.value);
        setConfirmTitle("Confirm role change");
        setConfirmMessage(
          `Are you sure you want to make ${userName} a ${roleLabel}?`,
        );
        const confirmed = await confirmRoleChange();
        if (!confirmed) {
          return;
        }
      }

      updateMember({
        variables: {
          organizationId: organization.id,
          role: option.value,
          userId: userId,
        },
      });
    },
    [
      canEditPermissions,
      confirmRoleChange,
      currentUser.id,
      member.role,
      organization.id,
      setConfirmMessage,
      setConfirmTitle,
      updateMember,
      userId,
      userName,
    ],
  );

  const saveChanges = () => {
    if (
      debouncedEmployeeId !== member.employeeId ||
      debouncedPosition !== member.position ||
      debouncedLocation !== member.location
    ) {
      updateMember({
        variables: {
          organizationId: organization.id,
          userId: userId,
          employeeId: employeeId,
          position: position,
          location,
        },
      });
    }
  };

  const saveUser = () => {
    if (
      debouncedFirstName !== member.user?.firstName ||
      debouncedLastName !== member.user?.lastName
    ) {
      updateUser({
        variables: {
          userId: userId,
          firstName,
          lastName,
        },
      });
    }
  };

  useEffect(() => {
    saveChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmployeeId, debouncedPosition, debouncedLocation]);
  useEffect(() => {
    saveUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFirstName, debouncedLastName]);

  const handleChangeStartdate = (date: Date | null) => {
    updateMember({
      variables: {
        organizationId: organization.id,
        startDate: date ? moment(date).format("YYYY-MM-DD") : "-1",
        userId: userId,
      },
    });
  };

  const handleAddTeam = (option: UserComboboxOption | null) => {
    if (option?.type === UserComboboxOptionType.TEAM && member.user?.id) {
      addTeamMember({
        variables: {
          teamId: option.id,
          userId: member.user.id,
        },
      });
    }
  };
  const handleRemoveTeam =
    (id: number) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (member.user?.id) {
        removeTeamMember({
          variables: {
            teamId: id,
            userId: member.user.id,
          },
        });
      }
    };

  return (
    <Modal aria-label="Org settings member modal" onClose={onClose}>
      <div className="p-6 flex flex-col gap-4">
        <ModalTitle onClose={onClose}>
          <div className="flex items-center gap-2">
            Edit {userName}
            {(loadingMutation || loadingUpdateUser) && (
              <Loading mini size={4} />
            )}
          </div>
        </ModalTitle>

        <div className="flex gap-4 items-center">
          <div className="w-24 text-sm font-medium">Name</div>
          <div className="flex items-center gap-1">
            <Input
              value={firstName || ""}
              onChange={(e) => setFirstName(e.target.value)}
              aria-label="Member first name"
              placeholder="First"
              className="w-48"
            />
            <Input
              value={lastName || ""}
              onChange={(e) => setLastName(e.target.value)}
              aria-label="Member last name"
              placeholder="Last"
              className="w-48"
            />
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="w-24 text-sm font-medium mt-1.5">Managers</div>
          <div className="text-sm flex flex-col gap-2">
            {managers.map((manager) => (
              <div className="w-full flex items-center gap-4" key={manager.id}>
                <MemberManagerCombobox
                  key={manager.id}
                  manager={manager}
                  user={user}
                  organization={organization}
                  excludeUserIds={excludeUserIds}
                  primaryManagerId={primaryManagerId}
                />
              </div>
            ))}
            <div className="w-48" key={null}>
              <MemberManagerCombobox
                key={null}
                manager={null}
                user={user}
                organization={organization}
                excludeUserIds={excludeUserIds}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <div className="w-24 text-sm font-medium mt-1.5">
            {label("team", { pluralize: true, capitalize: true })}
          </div>
          <div className="text-sm">
            {teams.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                {teams.map((team) => (
                  <div
                    className="flex items-center gap-2 border border-gray-300 rounded-md p-1 pl-2"
                    key={team.id}
                  >
                    {team.title}
                    <button
                      className="text-gray-500 p-0.5 rounded hover:bg-gray-100"
                      onClick={handleRemoveTeam(team.id)}
                    >
                      <TopicflowIcon type="close" className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="w-48" key={null}>
              <UserCombobox
                options={teamOptions}
                loading={teamLoading}
                value={null}
                onChangeValue={handleAddTeam}
                placeholder={`Add ${label("team")}`}
                query={query}
                onChangeQuery={setQuery}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <div className="w-24 text-sm font-medium">Employee ID</div>
          <Input
            value={employeeId || ""}
            onChange={(e) => setEmployeeId(e.target.value)}
            aria-label="Member employee id"
            placeholder="No id"
            className="w-48"
          />
        </div>
        <div className="flex gap-4 items-center">
          <div className="w-24 text-sm font-medium">Position</div>
          <Input
            value={position || ""}
            onChange={(e) => setPosition(e.target.value)}
            aria-label="Member position"
            placeholder="No position"
            className="w-48"
          />
        </div>
        <div className="flex gap-4 items-center">
          <div className="w-24 text-sm font-medium">Location</div>
          <Input
            value={location || ""}
            onChange={(e) => setLocation(e.target.value)}
            aria-label="Member location"
            placeholder="No location"
            className="w-48"
          />
        </div>
        <div className="flex gap-4 items-center">
          <div className="w-24 text-sm font-medium">Start date</div>
          <InputDatePicker
            date={member.startDate}
            onChangeDate={handleChangeStartdate}
            placeholderText="No start date"
            className="w-48"
          />
        </div>
        {canViewPermissions && (
          <div className="flex gap-4 items-center">
            <div className="w-24 text-sm font-medium">Permissions</div>
            {currentUser.id === userId ? (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                {getRoleLabel(member.role)}
                <Tooltip text="You cannot edit your own role.">
                  <span>
                    <TopicflowIcon
                      type="info"
                      className="h-4 w-4 text-gray-500"
                    />
                  </span>
                </Tooltip>
              </div>
            ) : (
              <DeprecatedSelect
                options={roleOptions}
                value={member.role}
                onChange={handleChangeRole}
                aria-label="Member role select"
                className="w-48"
                disabled={!canEditPermissions}
              />
            )}
          </div>
        )}
        <ConfirmRoleChangeDialog />
      </div>
    </Modal>
  );
};

const MemberEditDialog = ({
  organization,
  member,
  memberId,
  onClose,
}: {
  organization: any;
  member?: OrganizationMemberFragment;
  memberId?: number;
  onClose: () => void;
}) => {
  const { data, loading, error } = useQuery<
    GetOrganizationMemberQueryQuery,
    GetOrganizationMemberQueryQueryVariables
  >(getOrganizationMemberQuery, {
    variables: { organizationId: organization.id, userId: memberId || -1 },
    skip: memberId === undefined,
  });
  const loadingDebounced = useDebounce(loading, 1000);

  if (memberId === undefined && member === undefined) {
    throw new Error("Either member or memberId must be provided");
  }

  if (member) {
    return (
      <MemberEditDialogContent
        organization={organization}
        member={member}
        onClose={onClose}
      />
    );
  }

  if (loadingDebounced) {
    return (
      <Modal aria-label="Org settings member modal" onClose={onClose}>
        <div className="p-6 flex flex-col gap-4">
          <ModalTitle onClose={onClose}>Edit Member</ModalTitle>
          <Loading>Loading...</Loading>
        </div>
      </Modal>
    );
  }

  const members = data?.organization?.members
    ? assertEdgesNonNull(data.organization.members)
    : [];
  const loadedMember = members.at(0);

  if (!loadedMember || error) {
    return (
      <Modal aria-label="Org settings member modal" onClose={onClose}>
        <div className="p-6 flex flex-col gap-4">
          <ModalTitle onClose={onClose}>Edit Member</ModalTitle>
          The member you are looking for does not exist.
        </div>
      </Modal>
    );
  }

  return (
    <MemberEditDialogContent
      organization={organization}
      member={loadedMember}
      onClose={onClose}
    />
  );
};

export default MemberEditDialog;
